// frontend/src/components/MobileFooterNav.js
import React, { useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { useCartStatus } from "../hooks/useCart";
import { FiHome, FiShoppingCart, FiCheckSquare, FiBarChart2, FiUser } from "react-icons/fi";
import routeRegistry from "../services/routeRegistry";
import { useTranslation } from "../i18n";

const MobileFooterNav = () => {
  const { t } = useTranslation();
  const { user, userRoles, isSuperuser } = useContext(AuthContext);
  const location = useLocation();
  // Use new cart hook to get cart data
  const { itemCount: totalItems, totalPrice } = useCartStatus();

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Check route permissions
  const userRoleNames = Array.isArray(userRoles) ? userRoles : [];
  const effectiveRoles = isSuperuser ? [...userRoleNames, 'admin'] : userRoleNames;
  const hasProductCatalogAccess = user && routeRegistry.hasAccess('/', effectiveRoles);
  const hasCartAccess = user && routeRegistry.hasAccess('/cart', effectiveRoles);
  const hasCheckoutAccess = user && routeRegistry.hasAccess('/checkout', effectiveRoles);
  const hasDashboardAccess = user && routeRegistry.hasAccess('/dashboard', effectiveRoles);

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t border-gray-200 py-2 z-50" style={{ height: '60px' }}>
      <div className="flex justify-around items-center h-full">
        {/* Home - Show for logged in users with access OR logged out users */}
        {(hasProductCatalogAccess || !user) && (
          <div className="text-center">
            <Link
              to="/products"
              className={`flex flex-col items-center ${isActive('/products') || isActive('/') ? 'text-primary' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <FiHome className="text-xl mb-1" />
              <span className="text-xs">{t("Home")}</span>
            </Link>
          </div>
        )}

        {hasCartAccess && (
          <div className="text-center">
            <Link
              to="/cart"
              className={`flex flex-col items-center ${isActive('/cart') ? 'text-primary' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <div className="relative">
                <FiShoppingCart className="text-xl mb-1" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 text-xs bg-red-600 text-white rounded-full">
                    {totalItems > 9 ? '9+' : totalItems}
                  </span>
                )}
              </div>
              <span className="text-xs">{totalItems > 0 ? `£${totalPrice.toFixed(2)}` : t('Cart')}</span>
            </Link>
          </div>
        )}

        {user ? (
          <>
            {hasCheckoutAccess && (
              <div className="text-center">
                <Link
                  to="/checkout"
                  className={`flex flex-col items-center ${isActive('/checkout') ? 'text-primary' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <FiCheckSquare className="text-xl mb-1" />
                  <span className="text-xs">{t("Checkout")}</span>
                </Link>
              </div>
            )}
            {hasDashboardAccess && (
              <div className="text-center">
                <Link
                  to="/dashboard"
                  className={`flex flex-col items-center ${isActive('/dashboard') ? 'text-primary' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <FiBarChart2 className="text-xl mb-1" />
                  <span className="text-xs">{t("Dashboard")}</span>
                </Link>
              </div>
            )}
          </>
        ) : (
          <div className="text-center">
            <Link
              to="/login"
              className={`flex flex-col items-center ${isActive('/login') ? 'text-primary' : 'text-blue-600 hover:text-blue-700'}`}
            >
              <FiUser className="text-xl mb-1" />
              <span className="text-xs">{t("Login")}</span>
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default MobileFooterNav;